import { makeObservable, reaction } from 'mobx';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import VectorSource from 'ol/source/Vector';
import { MarineStore } from '@shared/marine/MarineStore';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { latLonToEN } from '@openlayers/ol-utils';
import Layer from 'ol/layer/Layer';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';
import { LineString } from 'ol/geom';
import OlMap from 'ol/Map';
import { PrintJsonDesktopMouseInteraction } from '@openlayers/interaction/PrintJsonDesktopMouseInteraction';

class TrailLayer extends Layer {
    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            style: {
                'stroke-width': 1,
                'stroke-color': [0, 0, 0, 0.3],
            },
            variables: {},
        });
    }
}

export class MarineLayer {
    featureType: FeatureType = 'marine-vessel';
    addedToMap = false;

    layer: WebGLPointsLayer<any> = null;
    trailLayer: TrailLayer = null;

    layerSource: VectorSource<Feature<Point>> = new VectorSource<Feature<Point>>({
        attributions: '',
        features: [],
        useSpatialIndex: true,
    });

    mouseInteraction: PrintJsonDesktopMouseInteraction = null;

    constructor(readonly store: MarineStore, private readonly map: OlMap) {
        makeObservable(this);

        this.mouseInteraction = new PrintJsonDesktopMouseInteraction(map);

        reaction(
            () => this.store.items,
            () => {
                try {
                    this.redrawAll();
                } catch (e) {
                    console.log('e:', e);
                }
            }
        );
    }

    addLayersToMap = () => {
        if (this.addedToMap) return;
        if (this.layer == null) {
            this.layer = new WebGLPointsLayer({
                source: this.layerSource,
                style: {
                    'icon-src':
                        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAlCAMAAAADS4u8AAADAFBMVEX///8AAAACAgIDAwMEBAQFBQUGBgYHBwcICAgJCQkKCgoLCwsMDAwNDQ0ODg4PDw8QEBARERESEhITExMUFBQVFRUWFhYXFxcYGBgZGRkaGhobGxscHBwdHR0eHh4fHx8gICAhISEiIiIjIyMkJCQlJSUmJiYnJycoKCgpKSkqKiorKyssLCwtLS0uLi4vLy8wMDAxMTEyMjIzMzM0NDQ1NTU2NjY3Nzc4ODg5OTk6Ojo7Ozs8PDw9PT0+Pj4/Pz9AQEBBQUFCQkJDQ0NERERFRUVGRkZHR0dISEhJSUlKSkpLS0tMTExNTU1OTk5PT09QUFBRUVFSUlJTU1NUVFRVVVVWVlZXV1dYWFhZWVlaWlpbW1tcXFxdXV1eXl5fX19gYGBhYWFiYmJjY2NkZGRlZWVmZmZnZ2doaGhpaWlqampra2tsbGxtbW1ubm5vb29wcHBxcXFycnJzc3N0dHR1dXV2dnZ3d3d4eHh5eXl6enp7e3t8fHx9fX1+fn5/f3+AgICBgYGCgoKDg4OEhISFhYWGhoaHh4eIiIiJiYmKioqLi4uMjIyNjY2Ojo6Pj4+QkJCRkZGSkpKTk5OUlJSVlZWWlpaXl5eYmJiZmZmampqbm5ucnJydnZ2enp6fn5+goKChoaGioqKjo6OkpKSlpaWmpqanp6eoqKipqamqqqqrq6usrKytra2urq6vr6+wsLCxsbGysrKzs7O0tLS1tbW2tra3t7e4uLi5ubm6urq7u7u8vLy9vb2+vr6/v7/AwMDBwcHCwsLDw8PExMTFxcXGxsbHx8fIyMjJycnKysrLy8vMzMzNzc3Ozs7Pz8/Q0NDR0dHS0tLT09PU1NTV1dXW1tbX19fY2NjZ2dna2trb29vc3Nzd3d3e3t7f39/g4ODh4eHi4uLj4+Pk5OTl5eXm5ubn5+fo6Ojp6enq6urr6+vs7Ozt7e3u7u7v7+/w8PDx8fHy8vLz8/P09PT19fX29vb39/f4+Pj5+fn6+vr7+/v8/Pz9/f3+/v7///+VceJeAAAAAXRSTlMAQObYZgAAAAFiS0dE/6UH8sUAAAAJcEhZcwAACxMAAAsTAQCanBgAAACgSURBVDjLzdLLCoJQFIVhV0EEubHgIIhGWVYQNQny/R+tpAaGR/1Pg2iNPzb7FkX/kzzG9KQZp1IAVQDVhVPlnMo41ZTT8eFaVAFUZ06VcaqYU004HRiuS1VzqopTOS89+Kj/3f0Uv/tPIpaG7pFc8bKvDuackrK3N93ATpukI3LRWtjwFraf27VeWHRPcS/TpZklzrlsXTyzO17r7676AMDwCaZ7XzxyAAAAAElFTkSuQmCC',
                    'icon-height': 42,
                    'icon-width': 42,
                    'icon-scale': 0.35,
                    'icon-opacity': 0.7,
                },
            });
            this.layer.set('id', this.featureType);
        }

        if (this.trailLayer == null) {
            this.trailLayer = new TrailLayer({
                source: new VectorSource(),
            });
        }

        this.map.addLayer(this.layer);
        this.layer.setZIndex(1);
        this.map.addLayer(this.trailLayer);
        this.trailLayer.setZIndex(this.layer.getZIndex() + 2);
        this.addedToMap = true;
    };

    removeLayersFromMap = () => {
        if (!this.addedToMap) return;
        this.addedToMap = false;

        this.map.removeLayer(this.layer);
        this.map.removeLayer(this.trailLayer);
        this.layer.dispose();
        this.trailLayer.dispose();
        this.layer = null;
        this.trailLayer = null;
    };

    clearSource = () => {
        if (this.layerSource) {
            this.layerSource.clear(true);
        }
        if (this.trailLayer) {
            (this.trailLayer.getSource() as VectorSource).clear();
        }
    };

    redrawAll = () => {
        this.clearSource();

        if (this.store.items == null || this.store.items.length === 0) {
            this.removeLayersFromMap();
            return;
        } else {
            this.addLayersToMap();
        }

        const features = this.store.items.map(v => {
            const feature = new Feature({
                id: v.mmsi,
                data: v.properties,
                type: this.featureType,
                geometry: new Point(latLonToEN(v.geometry.coordinates[1], v.geometry.coordinates[0])),
                mouseInteraction: this.mouseInteraction,
            });
            return feature;
        });

        if (this.store.previousItems) {
            const trailFeatures = [];

            this.store.previousItems.forEach(prev => {
                const latest = this.store.items.find(v => v.mmsi === prev.mmsi);
                if ((latest && latest.geometry.coordinates[0] !== prev.geometry.coordinates[0]) || latest.geometry.coordinates[1] !== prev.geometry.coordinates[1]) {
                    trailFeatures.push(
                        new Feature({
                            id: prev.mmsi,
                            geometry: new LineString([
                                latLonToEN(prev.geometry.coordinates[1], prev.geometry.coordinates[0]),
                                latLonToEN(latest.geometry.coordinates[1], latest.geometry.coordinates[0]),
                            ]),
                        })
                    );
                }
            });
            (this.trailLayer.getSource() as VectorSource).addFeatures(trailFeatures);
        }
        this.layerSource.addFeatures(features);
    };
}
